import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import moment from 'moment';
import { fetchCustomers, getAging, getAgingReport } from "../../utils/api/api";
import Select from 'react-select';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Line } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import {
    Calendar,
    X,
    Filter,
    Printer,
    FileSpreadsheet,
    AlertCircle,
    Clock
} from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../../elements/Modal';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);


const AgingReport = () => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [info, setInfo] = useState([]);
    const [open, setOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [selectedRange, setSelectedRange] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [report, setReport] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const handleStartDateChange = (date) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
    };

    const printTable = () => {
        const doc = new jsPDF("l", "mm", "a4");

        doc.text("Aging Report", 14, 10);

        const table = document.querySelector("#agingReportTable table");

        if (!table) {
            console.error("Table not found");
            return;
        }

        const headers = [];
        const data = [];

        const headerCells = table.querySelectorAll("thead tr th");
        headerCells.forEach((th) => headers.push(th.innerText.trim()));

        const rows = table.querySelectorAll("tbody tr");
        rows.forEach((row) => {
            const rowData = [];
            row.querySelectorAll("td").forEach((td) => {
                rowData.push(td.innerText.trim());
            });
            data.push(rowData);
        });

        const footer = table.querySelector("tfoot tr");
        if (footer) {
            const footerData = [];
            footer.querySelectorAll("td").forEach((td) => {
                footerData.push(td.innerText.trim());
            });
            data.push(footerData);
        }

        doc.autoTable({
            head: [headers],
            body: data,
            startY: 20,
            theme: "grid",
            styles: {
                fontSize: 8,
                cellPadding: 2,
            },
            headStyles: {
                fillColor: [59, 130, 246], // Blue color
                textColor: 255,
                fontStyle: "bold",
            },
        });

        doc.save("Aging_Report.pdf");
    };

    const handleSubmit = () => {
        if (startDate && endDate) {
            setIsLoading(true);
            const formattedStartDate = moment(startDate).format("YYYY-MM-DD");
            const formattedEndDate = moment(endDate).format("YYYY-MM-DD");

            getAgingReport(formattedStartDate, formattedEndDate)
                .then((data) => {
                    setReportData(data);
                })
                .catch((error) => {
                    console.error("Error fetching report:", error);
                })
                .finally(() => {
                    setIsLoading(false);
                });

            setIsModalOpen(false);
        } else {
            toast.error("Please select both start and end dates.");
        }
    };

    const exportExcel = () => {
        if (!report) {
            alert("No data available to export.");
            return;
        }
        const dataToExport = Object.entries(report).map(([key, customer], index) => ({
            "#": index + 1,
            Customer: customer.customer,
            Email: customer.email || "N/A",
            Phone: customer.phone_number || "N/A",
            "0-30 days": customer["0-30 days"],
            "31-60 days": customer["31-60 days"],
            "61-90 days": customer["61-90 days"],
            "91-180 days": customer["91-180 days"],
            "181-360 days": customer["181-360 days"],
            "360+ days": customer["360+ days"],
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Aging Report");
        XLSX.writeFile(workbook, "aging-report.xlsx");
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const prepareChartData = () => {
        if (!reportData || !reportData.invoice_collections) return {};

        const expectedCollectionsArray = reportData.invoice_collections.expected_collections_daily || [];
        const actualCollectionsArray = reportData.invoice_collections.actual_collections_daily || [];

        const startMoment = moment(startDate);
        const endMoment = moment(endDate);

        const allDates = [];
        let currentDate = startMoment.clone();

        while (currentDate <= endMoment) {
            allDates.push(currentDate.format('YYYY-MM-DD'));
            currentDate.add(1, 'day');
        }

        const expectedCollections = allDates.map(date => {
            const item = expectedCollectionsArray.find(item => item.day === date);
            return item ? parseFloat(item.expectedCollection) : null;
        });

        const actualCollections = allDates.map(date => {
            const item = actualCollectionsArray.find(item => item.day === date);
            return item ? item.actualCollection : null;
        });

        return {
            labels: allDates,
            datasets: [
                {
                    label: 'Expected Collection',
                    data: expectedCollections,
                    borderColor: '#3b82f6', // Modernized blue
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    fill: true,
                    tension: 0.4,
                    borderWidth: 2,
                },
                {
                    label: 'Actual Collection',
                    data: actualCollections,
                    borderColor: '#f43f5e', // Modernized red
                    backgroundColor: 'rgba(244, 63, 94, 0.1)',
                    fill: true,
                    tension: 0.4,
                    borderWidth: 2,
                },
            ],
        };
    };

    const rangeOptions = [
        { value: "0-30", label: "0-30 days" },
        { value: "31-60", label: "31-60 days" },
        { value: "61-90", label: "61-90 days" },
        { value: "91-180", label: "91-180 days" },
        { value: "181-360", label: "181-360 days" },
        { value: "360+", label: "360+ days" },
    ];

    useEffect(() => {
        const getCustomers = async () => {
            const payload = {
                page: 1,
                pageSize: 2000,
            };
            try {
                const response = await fetchCustomers(payload);
                const formattedCustomers = response?.customers?.map(customer => ({
                    value: customer.id,
                    label: `${customer.first_name} ${customer.last_name}`,
                })) || [];

                setCustomers(formattedCustomers);
            } catch (error) {
                console.error("Error fetching customers:", error);
            }
        };

        getCustomers();
    }, []);

    const getReport = async () => {
        setIsLoading(true);
        try {
            const filters = {
                customer_id: selectedCustomer?.value || null,
                start_date: startDate ? moment(startDate).format("YYYY-MM-DD") : null,
                end_date: endDate ? moment(endDate).format("YYYY-MM-DD") : null,
                range: selectedRange?.value || null,
            };

            const data = await getAging(filters);
            setReport(data.agingReport);
            setInfo(data.totals);
            setOpen(false);
            setSelectedCustomer(null);
            setSelectedRange(null);
        } catch (error) {
            console.error("Error fetching report:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getReport();
    }, []);

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            borderRadius: '0.75rem',
            border: '1px solid #e5e7eb',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#d1d5db'
            },
            padding: '4px',
            minHeight: '42px'
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: '0.75rem',
            overflow: 'hidden',
            boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.1)'
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#f3f4f6' : null,
            '&:active': {
                backgroundColor: '#dbeafe'
            },
            padding: '10px 14px'
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0 12px'
        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize: '0.875rem'
        }),
        placeholder: (provided) => ({
            ...provided,
            fontSize: '0.875rem'
        }),
        indicatorSeparator: () => ({
            display: 'none'
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: '#9ca3af',
            '&:hover': {
                color: '#6b7280'
            }
        })
    };

    const datePickerClassName = "w-full px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm text-sm";

    return (
        <motion.div
            className="w-full mt-4"
        >
            <motion.div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden mb-6"
            >
                <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 dark:text-blue-400">
                            <Calendar size={16} />
                        </div>
                        <h2 className="font-medium text-gray-900 dark:text-white">Invoice Collections</h2>
                    </div>
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setIsModalOpen(true)}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium transition-colors shadow-sm"
                    >
                        <Calendar size={14} />
                        <span>Select Date Range</span>
                    </motion.button>
                </div>

                {reportData ? (
                    <div className="p-5">
                        <Line data={prepareChartData()} options={{
                            responsive: true,
                            maintainAspectRatio: true,
                            plugins: {
                                title: {
                                    display: false
                                },
                                legend: {
                                    position: 'top',
                                    labels: {
                                        usePointStyle: true,
                                        boxWidth: 6,
                                        padding: 20,
                                        font: {
                                            size: 12
                                        }
                                    }
                                },
                                tooltip: {
                                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                                    titleColor: '#fff',
                                    bodyColor: '#fff',
                                    cornerRadius: 8,
                                    padding: 12,
                                    boxPadding: 6,
                                    usePointStyle: true,
                                },
                            },
                            scales: {
                                x: {
                                    grid: {
                                        display: false,
                                    },
                                    ticks: {
                                        font: {
                                            size: 11
                                        },
                                        maxRotation: 45,
                                        minRotation: 45
                                    }
                                },
                                y: {
                                    ticks: {
                                        callback: function (value) {
                                            return 'Kes ' + value.toLocaleString();
                                        },
                                        font: {
                                            size: 11
                                        }
                                    },
                                    grid: {
                                        color: 'rgba(0, 0, 0, 0.06)',
                                    },
                                },
                            },
                        }} />

                        <div className="mt-4 flex flex-wrap gap-4">
                            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center text-blue-600 dark:text-blue-400">
                                    <TrendingUp size={18} />
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">Expected Collection</div>
                                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                        {reportData.invoice_collections?.total_expected_collection ?
                                            `Kes ${parseFloat(reportData.invoice_collections.total_expected_collection).toLocaleString()}` :
                                            'Kes 0'}
                                    </div>
                                </div>
                            </div>

                            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center text-purple-600 dark:text-purple-400">
                                    <DollarSign size={18} />
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">Actual Collection</div>
                                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                        {reportData.invoice_collections?.total_actual_collection ?
                                            `Kes ${parseFloat(reportData.invoice_collections.total_actual_collection).toLocaleString()}` :
                                            'Kes 0'}
                                    </div>
                                </div>
                            </div>

                            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center text-green-600 dark:text-green-400">
                                    <PercentIcon size={18} />
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">Collection Rate</div>
                                    <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                        {reportData.invoice_collections?.total_expected_collection && reportData.invoice_collections?.total_actual_collection ?
                                            `${((parseFloat(reportData.invoice_collections.total_actual_collection) / parseFloat(reportData.invoice_collections.total_expected_collection)) * 100).toFixed(1)}%` :
                                            '0%'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12">
                        <div className="w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-500 dark:text-blue-400 mb-4">
                            <Calendar size={24} />
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 mb-1">No date range selected</p>
                        <p className="text-sm text-gray-400 dark:text-gray-500 max-w-md text-center">
                            Select a date range to see the invoice collections data visualization.
                        </p>
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setIsModalOpen(true)}
                            className="mt-4 flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium transition-colors shadow-sm"
                        >
                            <Calendar size={14} />
                            <span>Select Dates</span>
                        </motion.button>
                    </div>
                )}
            </motion.div>

            <motion.div
                id="agingReportTable"
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
            >
                <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center text-amber-600 dark:text-amber-400">
                            <Clock size={16} />
                        </div>
                        <h2 className="font-medium text-gray-900 dark:text-white">Aging Report</h2>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 self-end sm:self-auto">
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setOpen(true)}
                            className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm transition-colors"
                        >
                            <Filter size={14} />
                            <span>Filter</span>
                        </motion.button>

                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={printTable}
                            className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm transition-colors"
                        >
                            <Printer size={14} />
                            <span>Print</span>
                        </motion.button>

                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={exportExcel}
                            className="flex items-center space-x-2 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm transition-colors shadow-sm"
                        >
                            <FileSpreadsheet size={14} />
                            <span>Export Excel</span>
                        </motion.button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                                className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full mb-4"
                            />
                            <p className="text-gray-500 dark:text-gray-400">Loading aging data...</p>
                        </div>
                    ) : report && Object.keys(report).length > 0 ? (
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300">
                                    <th className="px-4 py-3 text-left font-medium whitespace-nowrap">#</th>
                                    <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Customer</th>
                                    <th className="px-4 py-3 text-left font-medium whitespace-nowrap">0-30</th>
                                    <th className="px-4 py-3 text-left font-medium whitespace-nowrap">31-60</th>
                                    <th className="px-4 py-3 text-left font-medium whitespace-nowrap">61-90</th>
                                    <th className="px-4 py-3 text-left font-medium whitespace-nowrap">91-180</th>
                                    <th className="px-4 py-3 text-left font-medium whitespace-nowrap">181-360</th>
                                    <th className="px-4 py-3 text-left font-medium whitespace-nowrap">360+</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {Object.entries(report).map(([key, customer], index) => (
                                    <tr key={key} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                        <td className="pl-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{index + 1}.</td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap font-medium cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                            {customer.customer ? customer.customer.split(' ').slice(0, 2).join(' ') : 'N/A'}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{parseFloat(customer["0-30 days"] || 0).toFixed(1)}</td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{parseFloat(customer["31-60 days"] || 0).toFixed(1)}</td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{parseFloat(customer["61-90 days"] || 0).toFixed(1)}</td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{parseFloat(customer["91-180 days"] || 0).toFixed(1)}</td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{parseFloat(customer["181-360 days"] || 0).toFixed(1)}</td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{parseFloat(customer["360+ days"] || 0).toFixed(1)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            {info && (
                                <tfoot>
                                    <tr className="bg-gray-100 dark:bg-gray-700 font-medium">
                                        <td className="pl-4 py-3 text-left text-gray-900 dark:text-white">Total</td>
                                        <td className="px-4 py-3 text-left text-gray-900 dark:text-white"></td>
                                        <td className="px-4 py-3 text-left text-gray-900 dark:text-white">{parseFloat(info["0-30 days"] || 0).toFixed(1)}</td>
                                        <td className="px-4 py-3 text-left text-gray-900 dark:text-white">{parseFloat(info["31-60 days"] || 0).toFixed(1)}</td>
                                        <td className="px-4 py-3 text-left text-gray-900 dark:text-white">{parseFloat(info["61-90 days"] || 0).toFixed(1)}</td>
                                        <td className="px-4 py-3 text-left text-gray-900 dark:text-white">{parseFloat(info["91-180 days"] || 0).toFixed(1)}</td>
                                        <td className="px-4 py-3 text-left text-gray-900 dark:text-white">{parseFloat(info["181-360 days"] || 0).toFixed(1)}</td>
                                        <td className="px-4 py-3 text-left text-gray-900 dark:text-white">{parseFloat(info["360+ days"] || 0).toFixed(1)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12">
                            <div className="w-16 h-16 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center text-amber-500 dark:text-amber-400 mb-4">
                                <AlertCircle size={24} />
                            </div>
                            <p className="text-gray-500 dark:text-gray-400 mb-1">No aging data available</p>
                            <p className="text-sm text-gray-400 dark:text-gray-500 max-w-md text-center mb-4">
                                Apply filters to see the aging report data.
                            </p>
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setOpen(true)}
                                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium transition-colors shadow-sm"
                            >
                                <Filter size={14} />
                                <span>Apply Filters</span>
                            </motion.button>
                        </div>
                    )}
                </div>
            </motion.div>

            {isModalOpen && (
                <Modal title="Select Date Range" onClose={handleCloseModal}>
                    <div className="w-full h-[60vh] flex items-center flex-col space-y-4">
                        <div className=''>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Start Date
                            </label>
                            <DatePicker
                                selected={startDate}
                                onChange={handleStartDateChange}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                placeholderText="Select start date"
                                className={datePickerClassName}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                End Date
                            </label>
                            <DatePicker
                                selected={endDate}
                                onChange={handleEndDateChange}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate}
                                placeholderText="Select end date"
                                className={datePickerClassName}
                            />
                        </div>

                        <div className="pt-2 grid grid-cols-2 gap-3">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleCloseModal}
                                className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl transition-colors"
                            >
                                Cancel
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleSubmit}
                                className="px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl transition-colors shadow-sm"
                            >
                                Apply
                            </motion.button>
                        </div>
                    </div>
                </Modal>
            )}

            {open && (
                <Modal title="Filter Aging Report" onClose={() => setOpen(false)}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Customer
                            </label>
                            <Select
                                options={customers}
                                value={selectedCustomer}
                                onChange={setSelectedCustomer}
                                placeholder="Select Customer"
                                styles={customSelectStyles}
                                className="w-full"
                                isClearable
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Start Date
                            </label>
                            <DatePicker
                                selected={startDate}
                                onChange={(date) => setStartDate(date)}
                                placeholderText="Select start date"
                                className={datePickerClassName}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                End Date
                            </label>
                            <DatePicker
                                selected={endDate}
                                onChange={(date) => setEndDate(date)}
                                placeholderText="Select end date"
                                className={datePickerClassName}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Age Range
                            </label>
                            <Select
                                options={rangeOptions}
                                value={selectedRange}
                                onChange={setSelectedRange}
                                placeholder="Select Age Range"
                                styles={customSelectStyles}
                                className="w-full"
                                isClearable
                            />
                        </div>

                        <div className="pt-2 grid grid-cols-2 gap-3">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setOpen(false)}
                                className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl transition-colors"
                            >
                                Cancel
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={getReport}
                                className="px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl transition-colors shadow-sm"
                            >
                                Apply Filters
                            </motion.button>
                        </div>
                    </div>
                </Modal>
            )}
        </motion.div>
    );
};

// Add the missing icon components
const TrendingUp = ({ size, className }) => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
            <polyline points="17 6 23 6 23 12"></polyline>
        </svg>
    );
};

const DollarSign = ({ size, className }) => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
            <line x1="12" y1="1" x2="12" y2="23"></line>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
        </svg>
    );
};

const PercentIcon = ({ size, className }) => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
            <line x1="19" y1="5" x2="5" y2="19"></line>
            <circle cx="6.5" cy="6.5" r="2.5"></circle>
            <circle cx="17.5" cy="17.5" r="2.5"></circle>
        </svg>
    );
};

export default AgingReport;